/*
 * Copyright (c) 2024, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {useProducts} from '@salesforce/commerce-sdk-react'
import {REMOVE_UNAVAILABLE_CART_ITEM_DIALOG_CONFIG} from '@salesforce/retail-react-app/app/constants'
import ConfirmationModal from '../confirmation-modal'
import {useDisclosure} from '@salesforce/retail-react-app/app/components/shared/ui'
import {noop} from '@salesforce/retail-react-app/app/utils/utils'

// Custom Hooks
import {useGTM} from '@app/hooks/use-gtm'

// Context
import {useCheckout} from '../../pages/checkout/util/checkout-context'

/**
 * This Component determines if the provided products have become unavailable or out of stock or low stock that
 * can't be fulfilled and will prompt the users to remove them before proceeding any further
 *
 * @param productIds -  list of product ids to check for availability. This prop will be deprecated in the upcoming release.
 * Please use productItems prop
 * @param productItems -  basket product items. This will be ignored if productIds is passed
 * @param handleUnavailableProducts - callback function to handle what to do with unavailable products
 * @returns {JSX.Element} -  Conformation Modal Component
 *
 */
const UnavailableProductConfirmationModal = ({
    productIds = [],
    productItems = [],
    handleUnavailableProducts = noop,
    isCheckout,
    isCart
}) => {
    const unavailableItemIdsRef = useRef(null)
    const ids = productIds.length ? productIds : productItems.map((i) => i.productId)
    const inventoryValues = productItems?.length
        ? productItems
              .map(
                  (product) =>
                      `${product?.productId ?? ''}-${product?.inventoryId ?? ''}-${
                          product?.c_currentStockLevel
                      }`
              )
              .join(',')
        : ''
    const {isOpen, onOpen, onClose} = useDisclosure()
    const [productsInternal, setProductsInternal] = useState({})
    const [isCheckoutBegin, setIsCheckoutBegin] = useState(false)
    const {triggerCheckoutEvents} = useGTM()
    const checkoutContext = useCheckout()
    const setProducts = checkoutContext?.setProducts

    const {data: products, isLoading: isProductsLoading} = useProducts(
        {parameters: {ids: ids?.join(','), allImages: true, c_inventoryValues: inventoryValues}},
        {
            enabled: ids?.length > 0,
            onSuccess: (result) => {
                const resProductIds = []
                const unOrderableIds = []
                result.data?.forEach(({id}) => {
                    // when a product is unavailable, the getProducts will not return its product detail.
                    // we compare the response ids with the ones in basket to figure which product has become unavailable
                    resProductIds.push(id)

                    // when a product is orderable, but the quantity in the basket is more than the remaining stock
                    // we want to make sure it is removed before go to checkout page to avoid error when placing order
                    // we don't need to remove low stock/ out of stock from wishlist
                    if (productItems.length) {
                        // Find all product items with the same productId
                        const matchingProductItems = productItems.filter(
                            (item) => item.productId === id
                        )

                        // Iterate over each matching product item
                        matchingProductItems.forEach((productItem) => {
                            // Check if the item is not a wishlist item
                            const isWishlist = !!productItem?.type
                            if (
                                !isWishlist &&
                                (!productItem.inventoryId ||
                                    productItem?.quantity > productItem?.c_currentStockLevel)
                            ) {
                                // Add the itemId to unOrderableIds if the quantity exceeds the current stock level
                                if (!unOrderableIds.includes(productItem.itemId)) {
                                    unOrderableIds.push(productItem.itemId)
                                }
                            }
                            if (isCheckout || isCart) {
                                // remove products with invalid price
                                if (
                                    productItem?.price == null &&
                                    productItem?.priceAfterItemDiscount == null
                                ) {
                                    if (!unOrderableIds.includes(productItem.itemId)) {
                                        unOrderableIds.push(productItem.itemId)
                                    }
                                }
                            }
                        })
                    }
                })

                setProductsInternal(result?.data)

                // Filter unavailable product items by itemId
                const unavailableItemIds = productItems
                    .filter(
                        (item) =>
                            !resProductIds.includes(item.productId) ||
                            unOrderableIds.includes(item.itemId)
                    )
                    .map((item) => item.itemId)

                unavailableItemIdsRef.current = unavailableItemIds
            }
        }
    )

    useEffect(() => {
        if (unavailableItemIdsRef.current?.length > 0) {
            onOpen()
        }
    }, [unavailableItemIdsRef.current])

    useEffect(() => {
        /**
         * Asynchronously removes unavailable products from the system.
         *
         * This function handles the removal of products that are no longer available
         * by invoking the `handleUnavailableProducts` function with the current list
         * of unavailable product IDs. It utilizes an asynchronous operation to ensure
         * that the removal process is completed before proceeding further.
         *
         * @async
         * @function removeUnaivalableProducts
         * @returns {Promise<void>} A promise that resolves when the unavailable products
         *                          have been successfully handled.
         */
        async function removeUnaivalableProducts() {
            await handleUnavailableProducts(unavailableItemIdsRef.current)
        }

        if (!isOpen) {
            // remove unavailable products when user close the modal without clicking on the primary and secondary action
            removeUnaivalableProducts()
        }
    }, [isOpen])

    useEffect(() => {
        // In order to improve performance, the GTM integration for the begin_checkout event will trigger on this component.
        // in this way, we have the basket and products available in the context of the event.
        if (isCheckout && isCheckoutBegin == false && !isProductsLoading) {
            if (setProducts) {
                setProducts(products?.data)
            }
            triggerCheckoutEvents(products?.data, 'begin_checkout')
            setIsCheckoutBegin(true)
        }
    }, [isProductsLoading])

    return (
        <ConfirmationModal
            data-testid="unavailable-product-modal"
            closeOnEsc={false}
            closeOnOverlayClick={false}
            {...REMOVE_UNAVAILABLE_CART_ITEM_DIALOG_CONFIG}
            hideAlternateAction={true}
            onPrimaryAction={async () => {
                await handleUnavailableProducts(unavailableItemIdsRef.current)
                unavailableItemIdsRef.current = null
                onClose()
            }}
            onAlternateAction={async () => {
                await handleUnavailableProducts(unavailableItemIdsRef.current)
                unavailableItemIdsRef.current = null
                onClose()
            }}
            isOpen={isOpen}
            onOpen={() => {}}
            onClose={onClose}
        />
    )
}

UnavailableProductConfirmationModal.propTypes = {
    productItems: PropTypes.array,
    productIds: PropTypes.array,
    handleUnavailableProducts: PropTypes.func,
    isCheckout: PropTypes.bool,
    isCart: PropTypes.bool
}

export default UnavailableProductConfirmationModal
